import {
	connectToRouter,
	NavigationCallback,
	PredefinedPropOptionKind,
	route
} from "client/Navigation";
import {
	EditIntegrationAppQueryParams,
	EditIntegrationAppRouteParams
} from "./EditIntegrationAppPage";
import {
	EditIntegrationFlowRouteParams,
	NewIntegrationFlowQueryParams,
	NewIntegrationFlowRouteParams
} from "./EditIntegrationFlowPage";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import {
	Tabs,
	ToolbarAction
} from "client/Components";
import {
	AppActionContext,
	AppsTabContent
} from "./AppsTabContent";
import styles from "./OverViewPage.module.less";
import {
	AuthenticationActionContext,
	AuthenticationsTabContent
} from "./AuthenticationsTabContent";
import { ActionsWithContext } from "client_legacy/ndaw/actions";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

type OverViewPageProps = {
	onNavigate: NavigationCallback;
};

export const OverViewPage = (props: OverViewPageProps): ReactElement => {
	const {
		onNavigate
	} = props;

	const [activeTabId, setActiveTabId] = useState<TabId>("apps");
	const [topBarActionState, setTopBarActionState] = useState<IntegrationActionWithContext>(emptyState);

	useActiveSettingsMenuItem(SettingsPageKind.IntegrationBuilder);

	const {
		actions,
		context
	} = topBarActionState;

	const topBarActionsStateChangeHandler = useCallback(
		(
			actions: readonly ToolbarAction<ActionContext>[],
			context: ActionContext
		): void => {
			setTopBarActionState({actions, context});
		},
		[]
	);

	return (
		<SettingsPage
			title={localize("_IntegrationBuilder")}
			actions={actions}
			actionContext={context}
			hasSelection={
				context.selection
					.length !== 0
			}
		>
			<SettingsPage.Section height="stretch">
				<Tabs<TabId>
					activeTabId={activeTabId}
					onActiveTabIdChange={setActiveTabId}
					height="fill"
				>
					<Tabs.Tab<TabId>
						id="apps"
						title={localize("_Apps")}
					>
						<div className={styles.tabContent}>
							<AppsTabContent
								isActive={activeTabId === "apps"}
								onActionsStateChange={topBarActionsStateChangeHandler}
								onNavigate={onNavigate}
							/>
						</div>
					</Tabs.Tab>
					<Tabs.Tab<TabId>
						id="authentication"
						title={localize("_Authentications")}
					>
						<div className={styles.tabContent}>
							<AuthenticationsTabContent
								isActive={activeTabId === "authentication"}
								onActionsStateChange={topBarActionsStateChangeHandler}
							/>
						</div>
					</Tabs.Tab>
				</Tabs>
			</SettingsPage.Section>
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
OverViewPage.displayName = "OverViewPage";

type TabId =
	| "apps"
	| "authentication";

export const IntegrationBuilderOverViewForRouter = connectToRouter(
	OverViewPage,
	{
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

export const integrationBuilderRouting = {
	overview: "/IntegrationBuilder",
	editApp: route<EditIntegrationAppRouteParams, EditIntegrationAppQueryParams>`/IntegrationBuilder/Apps/${"appId"}`,
	newFlow: route<NewIntegrationFlowRouteParams, NewIntegrationFlowQueryParams>`/IntegrationBuilder/Apps/${"appId"}/Flows/New`,
	editFlow: route<EditIntegrationFlowRouteParams>`/IntegrationBuilder/Apps/${"appId"}/Flows/${"integrationId"}/Versions/${"versionId"}`
};

type ActionContext = AppActionContext | AuthenticationActionContext;

const emptyState: IntegrationActionWithContext = {
	actions: [],
	context: {
		selection: [],
		isMobile: false
	}
};

type IntegrationActionWithContext = ActionsWithContext<ActionContext, ToolbarAction<ActionContext>>;
